import { BBox, Feature, LineString, MultiLineString, Point, Position } from "geojson";
import geojsonRbush from "geojson-rbush";
import { MultiUndirectedGraph } from "graphology";
import minBy from "lodash/minBy";

import {
  bbox as turfBbox,
  destination as turfDestination,
  distance as turfDistance,
  length as turfLength,
  lineIntersect as turfLineIntersect,
  lineString as turfLineString,
  nearestPointOnLine as turfNearestPointOnLine,
  point as turfPoint,
} from "fond/turf";

export type NodeId = string;
export type EdgeId = string;

export interface PathEdge {
  length: number;
  featureId: string | number;
  coordinates: Position[];
}

type NodeAttributes = { coord: [number, number] };

export type PathGraph = MultiUndirectedGraph<NodeAttributes, PathEdge>;

/**
 * The default merge tolerance, in metres.
 */
const DEFAULT_MERGE_TOLERANCE_METERS = 1.0;

/** Expand a bbox by `meters` in every direction (using local degrees-per-meter at the bbox centre). */
function expandBbox(b: BBox, meters: number): BBox {
  const centre: Position = [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2];
  const [, northLat] = turfDestination(centre, meters, 0, { units: "meters" }).geometry.coordinates;
  const [eastLng] = turfDestination(centre, meters, 90, { units: "meters" }).geometry.coordinates;
  const dLat = northLat - centre[1];
  const dLng = eastLng - centre[0];
  return [b[0] - dLng, b[1] - dLat, b[2] + dLng, b[3] + dLat];
}

/**
 * A graph node merger for combining geospatially close nodes together.
 */
class NodeMerger {
  private index = geojsonRbush<Point, { nodeId: NodeId }>();

  private coords: Map<NodeId, [number, number]> = new Map();

  private nextId = 0;

  constructor(private toleranceMeters: number) {}

  getOrCreate(coord: Position): NodeId {
    const within = this.index
      .search(this.bboxAround(coord))
      .features.map((f) => ({
        nodeId: f.properties!.nodeId,
        distanceMeters: turfDistance(coord, this.coords.get(f.properties!.nodeId)!, { units: "meters" }),
      }))
      .filter((c) => c.distanceMeters <= this.toleranceMeters);

    // If there are multiple nodes within distance then we merge with the closer one. This can happen
    // in a situation like:
    //
    //    X1 ------ <coord> ----------- X2
    //
    // where coord is within toleranceMeters of both X1 and X2, but X1 and X2 are not within toleranceMeters of each other.
    const best = minBy(within, (c) => c.distanceMeters);
    if (best) return best.nodeId;

    const newId: NodeId = `n${this.nextId}`;
    this.nextId += 1;
    this.coords.set(newId, [coord[0], coord[1]]);
    this.index.insert(turfPoint(coord, { nodeId: newId }));
    return newId;
  }

  coordOf(nodeId: NodeId): [number, number] {
    return this.coords.get(nodeId)!;
  }

  // Search box of "radius" `toleranceMeters` around `coord`.
  private bboxAround(coord: Position): BBox {
    return expandBbox([coord[0], coord[1], coord[0], coord[1]], this.toleranceMeters);
  }
}

/**
 * A flattened LineString belonging to an input feature. MultiLineStrings produce
 * one Part per component.
 */
type Part = {
  featureId: string | number;
  suffix: string;
  coords: Position[];
};

/** Drops consecutive coordinates that are identical. */
function dedupConsecutive(coords: Position[]): Position[] {
  const out: Position[] = [];
  for (const c of coords) {
    const last = out[out.length - 1];
    if (!last || last[0] !== c[0] || last[1] !== c[1]) out.push(c);
  }
  return out;
}

/** Flattens features into one Part per LineString / MultiLineString component. */
function flattenToParts(features: Array<Feature<LineString | MultiLineString>>): Part[] {
  const parts: Part[] = [];

  const pushPart = (featureId: string | number, suffix: string, raw: Position[]): void => {
    const coords = dedupConsecutive(raw);
    if (coords.length < 2) return;
    parts.push({ featureId, suffix, coords });
  };

  for (const feature of features) {
    const featureId = (feature.id ?? feature.properties?.id) as string | number | undefined;
    if (!featureId) continue;

    if (feature.geometry.type === "LineString") {
      pushPart(featureId, "", feature.geometry.coordinates);
    } else if (feature.geometry.type === "MultiLineString") {
      feature.geometry.coordinates.forEach((part, j) => pushPart(featureId, `#${j}`, part));
    }
  }
  return parts;
}

/** A point at which to split a Part's polyline. */
type SplitPoint = {
  /** The split coordinate. */
  coord: Position;

  /** Index of the segment (in the part's coords) that contains this split point. */
  index: number;

  /** Distance along the line in meters. */
  location: number;
};

/**
 * Computes split points for each Part. The algorithm is symmetric across each unordered pair of
 * Parts: each contribution to part A is mirrored by the corresponding contribution to part B, so
 * that the resulting graph stays connected through every place where two lines meet (or nearly do).
 *
 * A Part `A` is split:
 * - where it crosses another Part `B`, and
 * - at the projection of any vertex of `B` that lies within `toleranceMeters` of `A`'s interior;
 *   when that vertex is interior to `B` (not an endpoint), `B` is also split at the vertex itself.
 *
 * In all cases, splits within `toleranceMeters` of the part's own endpoints are skipped, since the
 * endpoint already provides a graph node.
 */
function computeSplitPoints(parts: Part[], toleranceMeters: number): SplitPoint[][] {
  const lines = parts.map((p) => turfLineString(p.coords));
  const index = geojsonRbush<LineString, { partIdx: number }>();
  index.load(lines.map((line, i) => ({ ...line, properties: { partIdx: i } })));

  const projectOnto = (line: Feature<LineString>, coord: Position) => turfNearestPointOnLine(line, turfPoint(coord), { units: "meters" });

  // Per-part raw split coords, populated as we visit each unordered pair of parts.
  const rawSplitsByPart: Position[][] = parts.map(() => []);

  // Adds `coord` as a split of `parts[idx]` unless it falls within `toleranceMeters` of that part's own endpoints.
  const tryAddSplit = (idx: number, coord: Position): void => {
    const p = parts[idx];
    const start = p.coords[0];
    const end = p.coords[p.coords.length - 1];
    if (turfDistance(coord, start, { units: "meters" }) <= toleranceMeters) return;
    if (turfDistance(coord, end, { units: "meters" }) <= toleranceMeters) return;
    rawSplitsByPart[idx].push(coord);
  };

  // Visits each pair of parts whose bboxes are within `toleranceMeters` of each other, exactly once.
  for (let i = 0; i < parts.length; i += 1) {
    const candidates = index
      .search(expandBbox(turfBbox(lines[i]) as BBox, toleranceMeters))
      .features.map((f) => f.properties!.partIdx)
      .filter((j) => j > i);

    for (const j of candidates) {
      // (a) Crossings: `i` and `j` intersect.
      //
      //                j
      //                |
      //    i  ---------X---------
      //                |
      //                j
      //
      turfLineIntersect(lines[i], lines[j]).features.forEach((pt) => {
        const coord = pt.geometry.coordinates;
        tryAddSplit(i, coord);
        tryAddSplit(j, coord);
      });

      // (b) Vertex-near-line: a vertex of `j` is close to `i`'s interior (or the reverse).
      //
      //    i  ----------------------
      //                .  # <-- gap < threshold
      //    j           V
      //           ----- -----
      //        ---           ---
      //
      const considerVerticesOf = (a: number, b: number): void => {
        const bCoords = parts[b].coords;

        bCoords.forEach((vertex, k) => {
          // Find the nearest point on `a` to the vertex.
          const projection = projectOnto(lines[a], vertex);
          const { dist } = projection.properties as { dist: number };
          if (dist > toleranceMeters) return;

          tryAddSplit(a, projection.geometry.coordinates);
          const isEndpoint = k === 0 || k === bCoords.length - 1;
          if (!isEndpoint) tryAddSplit(b, vertex);
        });
      };

      considerVerticesOf(i, j);
      considerVerticesOf(j, i);
    }
  }

  // Deduplicate and sort the split points of each part.
  return rawSplitsByPart.map((rawCoords, i) => {
    if (rawCoords.length === 0) return [];

    const projected: SplitPoint[] = rawCoords.map((coord) => {
      const r = projectOnto(lines[i], coord);
      const props = r.properties as { index: number; location: number };
      return { coord, index: props.index, location: props.location };
    });
    projected.sort((a, b) => a.location - b.location);

    const deduped: SplitPoint[] = [];
    for (const sp of projected) {
      const last = deduped[deduped.length - 1];
      if (last && Math.abs(sp.location - last.location) <= toleranceMeters) continue;
      deduped.push(sp);
    }
    return deduped;
  });
}

/**
 * Slices line coordinates at each split point.
 */
function sliceCoords(coords: Position[], splits: SplitPoint[]): Position[][] {
  if (splits.length === 0) return [coords];

  const result: Position[][] = [];
  const same = (a: Position, b: Position): boolean => a[0] === b[0] && a[1] === b[1];
  const pushIfNew = (sub: Position[], coord: Position): void => {
    if (sub.length === 0 || !same(sub[sub.length - 1], coord)) sub.push(coord);
  };

  let cursor = 0;
  let current: Position[] = [coords[0]];

  for (const split of splits) {
    for (let i = cursor + 1; i <= split.index; i += 1) pushIfNew(current, coords[i]);
    pushIfNew(current, split.coord);
    if (current.length >= 2) result.push(current);
    current = [split.coord];
    cursor = split.index;
  }

  // Add the remaining segment (everything after the last split), if any.
  for (let i = cursor + 1; i < coords.length; i += 1) pushIfNew(current, coords[i]);
  if (current.length >= 2) result.push(current);

  return result;
}

/**
 * Builds an undirected weighted multigraph based on a collection of (Multi)LineStrings.
 *
 * The nodes of this graph are the endpoints of each LineString, where endpoints that
 * are within `mergeToleranceMeters` metres of each other are merged into a single node.
 *
 * The edges of this graph are the individual LineStrings, with a weight equal to their
 * geometric length.
 *
 * Each feature must have either `feature.id` or `feature.properties.id` defined to be included
 * in the graph. Any feature without these is skipped. It is the caller's responsibility to ensure
 * these IDs are unique across features.
 *
 * When `splitIntersectingPaths` is true, lines are also split at points where they cross or have vertices
 * close to other input lines.
 */
export function buildPathGraph(
  features: Array<Feature<LineString | MultiLineString>>,
  options: { mergeToleranceMeters?: number; splitIntersectingPaths?: boolean } = {}
): PathGraph {
  const tolerance = options.mergeToleranceMeters ?? DEFAULT_MERGE_TOLERANCE_METERS;
  const merger = new NodeMerger(tolerance);
  const graph = new MultiUndirectedGraph<NodeAttributes, PathEdge>();

  const addEdge = (coords: Position[], featureId: string | number, suffix: string): void => {
    if (coords.length < 2) return;
    const a = merger.getOrCreate(coords[0]);
    const b = merger.getOrCreate(coords[coords.length - 1]);

    const edgeId: EdgeId = `${featureId}${suffix}`;
    const length = turfLength(turfLineString(coords), { units: "meters" });
    graph.mergeNode(a, { coord: merger.coordOf(a) });
    graph.mergeNode(b, { coord: merger.coordOf(b) });
    graph.addEdgeWithKey(edgeId, a, b, { length, featureId, coordinates: coords });
  };

  const parts = flattenToParts(features);
  const splitsByPart = options.splitIntersectingPaths ? computeSplitPoints(parts, tolerance) : undefined;

  parts.forEach((p, i) => {
    const splits = splitsByPart?.[i];
    if (!splits || splits.length === 0) {
      addEdge(p.coords, p.featureId, p.suffix);
      return;
    }
    sliceCoords(p.coords, splits).forEach((sub, k) => addEdge(sub, p.featureId, `${p.suffix}@${k}`));
  });

  return graph;
}

/**
 * Return linestring coordinates for a path of edges through a PathGraph.
 *
 * The returned linestring is guaranteed to be oriented in the direction of the path,
 * regardless of the original orientation of the edges in the graph.
 */
export function buildPathCoordinates(graph: PathGraph, sourceNodeId: NodeId, edgeIds: EdgeId[]): Position[] {
  const out: Position[] = [];
  let cursor = sourceNodeId;
  for (const [i, edgeId] of edgeIds.entries()) {
    if (!graph.hasEdge(edgeId)) return [];

    // The PathGraph stores edge coordinates with an arbitrary orientation, so we may need to reverse
    // those coordinates to ensure the path flows from source to target. For example:
    //
    //   A -----> B <----- C <----- D
    //
    // If the path is [A, B, C, D] then the coordinates of the edge AB are oriented in the correct direction,
    // but the coordinates of BC and CD would be reversed.
    const { coordinates } = graph.getEdgeAttributes(edgeId);
    const ordered = graph.source(edgeId) === cursor ? coordinates : [...coordinates].reverse();
    out.push(...(i === 0 ? ordered : ordered.slice(1)));
    cursor = graph.opposite(cursor, edgeId);
  }

  return out;
}
